import type {
  PracticeRecordingFocusPointType,
  PracticeRecordingSegmentPointType,
  PracticeRecordingType,
  SegmentType,
  SentenceType,
} from 'types/types'
import { computed, nextTick, ref, watch } from 'vue'

import { useFont } from '@/composables/font'
import { formattedDate, practiceRecordingDescription } from '@/composables/format'
import { PLAYBACK_OVERLAY_TEXT_NAME, SENTENCE_ELEMENT_ID_SUFFIX, textSegmentId } from '@/constants/practiceConstants'
import store from '@/store'
import { AnnotatedSegment } from '@/util/AnnotatedSegment'
import { AnnotatedSentence } from '@/util/AnnotatedSentence'
import { regularStyle, SEGMENT_STYLES, userSpeakingStyle } from '@/util/TextStyles'

/** Compute relative ms offsets from an array of absolute-epoch focus point moments. */
export function computeRelativeOffsets(focusPoints: PracticeRecordingFocusPointType[]): number[] {
  if (!focusPoints.length) {
    return []
  }
  const baseMs = focusPoints[0].moment
  return focusPoints.map((fp) => fp.moment - baseMs)
}

/** Deduplicate sentences from focus points, preserving order of first appearance. */
export function deduplicateSentences(focusPoints: PracticeRecordingFocusPointType[]): SentenceType[] {
  const seen = new Set<string>()
  const result: SentenceType[] = []
  for (const fp of focusPoints) {
    if (fp.sentence && !seen.has(fp.sentence.id)) {
      seen.add(fp.sentence.id)
      result.push(fp.sentence)
    }
  }
  return result
}

/** Map each focus point index to its index in the deduplicated sentences array. */
export function buildFocusToSentenceMap(focusPoints: PracticeRecordingFocusPointType[], sentences: SentenceType[]): number[] {
  const idToIndex = new Map(sentences.map((s, i) => [s.id, i]))
  return focusPoints.map((fp) => idToIndex.get(fp.sentence?.id ?? '') ?? 0)
}

/** Find the current index given playback time in ms and sorted relative offsets. */
export function findCurrentIndex(currentMs: number, offsets: number[]): number {
  let idx = 0
  for (let i = offsets.length - 1; i >= 0; i--) {
    if (offsets[i] <= currentMs) {
      idx = i
      break
    }
  }
  return idx
}

/** @deprecated Use findCurrentIndex instead */
export const findCurrentFocusIndex = findCurrentIndex

/** Build a segmentation array (word counts per segment) from a focus point's segmentedSentence. */
function segmentationFromFocusPoint(fp: PracticeRecordingFocusPointType): number[] {
  if (!fp.segmentedSentence?.segments?.length) {
    return []
  }
  return [...fp.segmentedSentence.segments].sort((a: SegmentType, b: SegmentType) => a.index - b.index).map((s: SegmentType) => s.words)
}

/** Format milliseconds as m:ss (e.g., 5000 → "0:05", 150000 → "2:30"). */
export function formatMs(ms: number): string {
  const totalSeconds = Math.round(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

export interface OccurrenceChoice {
  attachId: string
  occurrences: Array<{ label: string; offsetMs: number }>
}

export function usePlaybackOverlay() {
  const visible = ref(false)
  const loading = ref(false)
  const recording = ref<PracticeRecordingType | null>(null)
  const currentFocusIndex = ref(0)
  const surroundLines = ref(0)
  const { fontSize: storedFontSize } = useFont()
  const overlayFontSize = ref(storedFontSize.value)

  // --- Derived data from focus points ---

  const focusPoints = computed<PracticeRecordingFocusPointType[]>(
    () => (recording.value?.focusPoints?.filter(Boolean) ?? []) as PracticeRecordingFocusPointType[],
  )

  const relativeOffsets = computed(() => computeRelativeOffsets(focusPoints.value))

  const sentences = computed(() => deduplicateSentences(focusPoints.value))

  const focusToSentenceMap = computed(() => buildFocusToSentenceMap(focusPoints.value, sentences.value))

  const currentSentenceIndex = computed(() => {
    const mapping = focusToSentenceMap.value
    if (!mapping.length) {
      return 0
    }
    return mapping[currentFocusIndex.value] ?? 0
  })

  const maxSurroundLines = computed(() => Math.max(0, sentences.value.length - 1))

  /** For each deduplicated sentence, the relative-ms offset of its first focus point. */
  const sentenceSeekOffsets = computed(() => {
    const offsets = relativeOffsets.value
    const map = focusToSentenceMap.value
    const result: number[] = new Array(sentences.value.length).fill(0)
    const seen = new Set<number>()
    for (let i = 0; i < map.length; i++) {
      const si = map[i]
      if (!seen.has(si)) {
        seen.add(si)
        result[si] = offsets[i]
      }
    }
    return result
  })

  /** For each deduplicated sentence, distinct visit offsets (for duplicate sentence handling).
   *  Consecutive focus points for the same sentence are one visit — a new occurrence is only
   *  counted when the student navigates away to a different sentence and comes back. */
  const sentenceOccurrences = computed(() => {
    const offsets = relativeOffsets.value
    const map = focusToSentenceMap.value
    const result: Array<Array<{ label: string; offsetMs: number }>> = sentences.value.map(() => [])
    let prevSentenceIdx = -1
    for (let i = 0; i < map.length; i++) {
      if (map[i] !== prevSentenceIdx) {
        result[map[i]].push({ label: formatMs(offsets[i]), offsetMs: offsets[i] })
        prevSentenceIdx = map[i]
      }
    }
    return result
  })

  const canJumpPrev = computed(() => currentSentenceIndex.value > 0)
  const canJumpNext = computed(() => currentSentenceIndex.value < sentences.value.length - 1)

  // --- Segment point tracking ---

  const segmentPoints = computed<PracticeRecordingSegmentPointType[]>(
    () => (recording.value?.segmentPoints?.filter(Boolean) ?? []) as PracticeRecordingSegmentPointType[],
  )

  /** Base timestamp (ms) for computing relative offsets — same as focus points[0].moment */
  const baseMs = computed(() => (focusPoints.value.length ? focusPoints.value[0].moment : 0))

  /** Relative offsets for ALL segment points (relative to focus point base). */
  const segmentRelativeOffsets = computed(() => {
    if (!segmentPoints.value.length || !focusPoints.value.length) {
      return []
    }
    return segmentPoints.value.map((sp) => sp.moment - baseMs.value)
  })

  /** Current segment point index based on playback time. */
  const currentSegmentPointIndex = ref(0)

  /** Current segment index within the current sentence (derived from the current segment point). */
  const currentSegmentIndex = computed(() => {
    const sps = segmentPoints.value
    if (!sps.length) {
      return -1
    }
    const sp = sps[currentSegmentPointIndex.value]
    if (!sp) {
      return -1
    }
    // Only return a segment index if the segment point belongs to the current sentence
    const currentSentence = sentences.value[currentSentenceIndex.value]
    if (!currentSentence || sp.sentence.id !== currentSentence.id) {
      return -1
    }
    return sp.segmentIndex
  })

  /** For each deduplicated sentence, the segmentation array from its focus point.
   *  Falls back to word-level segmentation (from segment points) for Reading mode,
   *  where there is no segmentedSentence on the focus point. */
  const segmentationsBySentence = computed<number[][]>(() => {
    // Build max segment index per sentence from segment points (word-by-word fallback)
    const maxSegmentIndexBySentence = new Map<string, number>()
    for (const sp of segmentPoints.value) {
      const current = maxSegmentIndexBySentence.get(sp.sentence.id) ?? -1
      if (sp.segmentIndex > current) {
        maxSegmentIndexBySentence.set(sp.sentence.id, sp.segmentIndex)
      }
    }

    const sentenceIdToFp = new Map<string, PracticeRecordingFocusPointType>()
    for (const fp of focusPoints.value) {
      if (fp.sentence && !sentenceIdToFp.has(fp.sentence.id)) {
        sentenceIdToFp.set(fp.sentence.id, fp)
      }
    }

    return sentences.value.map((s) => {
      const fp = sentenceIdToFp.get(s.id)
      console.log(
        `[playbackOverlay] sentence ${s.id}: fp=${!!fp}, segmentedSentence=${fp?.segmentedSentence?.id}, segments=${fp?.segmentedSentence?.segments?.length}`,
      )
      const fromFp = fp ? segmentationFromFocusPoint(fp) : []
      if (fromFp.length) {
        console.log(`[playbackOverlay] sentence ${s.id}: segmentation from FP = [${fromFp}]`)
        return fromFp
      }
      // Word-by-word fallback: each segment = 1 word (same as makeWordSegmentation)
      const maxIdx = maxSegmentIndexBySentence.get(s.id) ?? -1
      return maxIdx >= 0 ? new Array(maxIdx + 1).fill(1) : []
    })
  })

  /** Segmentations for the visible slice of sentences (for AnnotatedText). */
  const segmentations = computed(() => {
    const end = Math.min(sentences.value.length, currentSentenceIndex.value + surroundLines.value + 1)
    return segmentationsBySentence.value.slice(visibleStart.value, end)
  })

  /** All seek offsets for each segment point, indexed by [sentenceId][segmentIndex] → offsets[]. */
  const segmentSeekOffsets = computed(() => {
    const map = new Map<string, Map<number, number[]>>()
    for (let i = 0; i < segmentPoints.value.length; i++) {
      const sp = segmentPoints.value[i]
      if (!map.has(sp.sentence.id)) {
        map.set(sp.sentence.id, new Map())
      }
      const sentenceMap = map.get(sp.sentence.id)!
      if (!sentenceMap.has(sp.segmentIndex)) {
        sentenceMap.set(sp.segmentIndex, [])
      }
      sentenceMap.get(sp.segmentIndex)!.push(segmentRelativeOffsets.value[i])
    }
    return map
  })

  // --- Surround / visible slice ---

  const visibleStart = computed(() => Math.max(0, currentSentenceIndex.value - surroundLines.value))

  const visibleSentences = computed(() => {
    const end = Math.min(sentences.value.length, currentSentenceIndex.value + surroundLines.value + 1)
    return sentences.value.slice(visibleStart.value, end)
  })

  /** Look up all sentences for the recording's section from the store. */
  function sectionSentences(fp: PracticeRecordingFocusPointType): SentenceType[] {
    if (fp.aliyah?.id) {
      return store.getters.student.getAliyahSentences(fp.aliyah.id)
    }
    if (fp.haftarah?.reading?.id) {
      return store.getters.student.getHaftarahSentences(fp.haftarah.reading.id)
    }
    if (fp.sentenceGroup?.reading?.id) {
      return store.getters.student.getSentenceGroupSentences(fp.sentenceGroup.reading.id)
    }
    return []
  }

  /** Offset of the recording's first sentence within the full section (aliyah/haftarah/sg). */
  const sectionSentenceOffset = computed(() => {
    const fp = focusPoints.value[0]
    if (!fp?.sentence) {
      return 0
    }
    const allSentences = sectionSentences(fp)
    if (!allSentences.length) {
      return 0
    }
    const idx = allSentences.findIndex((s) => s.id === sentences.value[0]?.id)
    return idx >= 0 ? idx : 0
  })

  /** Absolute index of the first visible sentence, for AnnotatedText :start-index. */
  const startIndex = computed(() => sectionSentenceOffset.value + visibleStart.value)

  /** Blob URL for the current recording's audio. */
  const audioSrc = computed(() => {
    if (!recording.value) {
      return null
    }
    return store.getters.practiceRecordings.getCachedAudioUrl(recording.value.id)
  })

  // --- Sentence style callback for AnnotatedText ---
  // Sentence-level background is covered by segment backgrounds, so always plain.

  function sentenceStyle(_style: string, _annotatedSentence: unknown, _sentenceIndex: number): string {
    return regularStyle
  }

  // --- Segment style callback for AnnotatedText ---
  // Alternating phrase colors for all sentences (matches Practice view).
  // With segment tracking (Legacy): active segment gets playBackStyle highlight.

  function segmentStyle(
    _style: string,
    _annotatedSentence: AnnotatedSentence,
    _annotatedSegment: AnnotatedSegment,
    sentenceIndex: number,
    segmentIndex: number,
  ): string {
    const absoluteSentenceIndex = visibleStart.value + sentenceIndex
    if (segmentPoints.value.length && absoluteSentenceIndex === currentSentenceIndex.value && segmentIndex === currentSegmentIndex.value) {
      return userSpeakingStyle
    }
    return SEGMENT_STYLES[segmentIndex % 2]
  }

  // --- Suppress character-level coloration on the actively-highlighted segment ---
  // Inline styles from coloration would override the CSS-class-based playback highlight.

  function suppressColors(sentenceIndex?: number, segmentIndex?: number): boolean {
    if (sentenceIndex === undefined || segmentIndex === undefined) {
      return false
    }
    const absoluteSentenceIndex = visibleStart.value + sentenceIndex
    return (
      segmentPoints.value.length > 0 && absoluteSentenceIndex === currentSentenceIndex.value && segmentIndex === currentSegmentIndex.value
    )
  }

  // --- Clickable segment check (only segments with seek data are clickable) ---

  function clickableSegment(
    _annotatedSentence: AnnotatedSentence,
    _annotatedSegment: AnnotatedSegment,
    sentenceIndex: number,
    segmentIndex: number,
  ): boolean {
    const absoluteSentenceIndex = visibleStart.value + sentenceIndex
    const sentence = sentences.value[absoluteSentenceIndex]
    if (!sentence) {
      return false
    }
    const sentenceOffsets = segmentSeekOffsets.value.get(sentence.id)
    if (sentenceOffsets) {
      const result = sentenceOffsets.has(segmentIndex)
      console.log(`[clickableSegment] sentence=${sentence.id} seg=${segmentIndex} → segmentOffsets has=${result}`)
      return result
    }
    // Sentence-level fallback only for legacy recordings with no segment points at all.
    // Modern recordings have segment points; sentences without them weren't recorded.
    if (segmentPoints.value.length) {
      console.log(`[clickableSegment] sentence=${sentence.id} seg=${segmentIndex} → no offsets, has segment points elsewhere → false`)
      return false
    }
    const result = (sentenceOccurrences.value[absoluteSentenceIndex]?.length ?? 0) > 0
    console.log(`[clickableSegment] sentence=${sentence.id} seg=${segmentIndex} → legacy fallback → ${result}`)
    return result
  }

  // --- Segment click handler for scrubbing ---

  const pendingOccurrenceChoice = ref<OccurrenceChoice | null>(null)

  function seekToOccurrence(offsetMs: number) {
    seekTimeMs.value = offsetMs
    seekGeneration.value++
    pendingOccurrenceChoice.value = null
  }

  function clearPendingChoice() {
    pendingOccurrenceChoice.value = null
  }

  function handleSegmentClick(
    _annotatedSentence: AnnotatedSentence,
    _annotatedSegment: AnnotatedSegment,
    sentenceIndex: number,
    segmentIndex: number,
  ) {
    const absoluteSentenceIndex = visibleStart.value + sentenceIndex
    const sentence = sentences.value[absoluteSentenceIndex]
    if (!sentence) {
      console.log(`[handleSegmentClick] no sentence at absoluteIndex=${absoluteSentenceIndex}`)
      return
    }

    const attachId = textSegmentId(PLAYBACK_OVERLAY_TEXT_NAME, sentenceIndex, segmentIndex)

    // Try segment-level seeking first (from segment points)
    const sentenceOffsets = segmentSeekOffsets.value.get(sentence.id)
    const segmentOffsets = sentenceOffsets?.get(segmentIndex)
    if (segmentOffsets?.length) {
      if (segmentOffsets.length === 1) {
        seekTimeMs.value = segmentOffsets[0]
        seekGeneration.value++
      } else {
        pendingOccurrenceChoice.value = {
          attachId,
          occurrences: segmentOffsets.map((ms) => ({ label: formatMs(ms), offsetMs: ms })),
        }
      }
      return
    }

    // If segment-level data exists for this sentence but not this segment,
    // don't fall back — this segment was not recorded.
    if (sentenceOffsets) {
      return
    }

    // Fall back to sentence-level seeking (no segment points for this sentence)
    const occurrences = sentenceOccurrences.value[absoluteSentenceIndex]
    if (!occurrences?.length) {
      return
    }
    if (occurrences.length === 1) {
      seekTimeMs.value = occurrences[0].offsetMs
      seekGeneration.value++
    } else {
      pendingOccurrenceChoice.value = { attachId, occurrences }
    }
  }

  // --- Audio time tracking (called by PlaybackOverlay on timeupdate) ---

  /** Incremented each time a seek is requested, so the component can watch for changes even to the same offset. */
  const seekGeneration = ref(0)
  /** Target seek time in ms; component watches seekGeneration to trigger the actual audio seek. */
  const seekTimeMs = ref(0)

  function onAudioTimeUpdate(currentTimeMs: number) {
    currentFocusIndex.value = findCurrentIndex(currentTimeMs, relativeOffsets.value)
    if (segmentRelativeOffsets.value.length) {
      currentSegmentPointIndex.value = findCurrentIndex(currentTimeMs, segmentRelativeOffsets.value)
    }
  }

  function jumpToSentence(sentenceIndex: number) {
    const clamped = Math.max(0, Math.min(sentenceIndex, sentences.value.length - 1))
    seekTimeMs.value = sentenceSeekOffsets.value[clamped]
    seekGeneration.value++
  }

  function jumpFirst() {
    jumpToSentence(0)
  }

  function jumpPrev() {
    if (canJumpPrev.value) {
      jumpToSentence(currentSentenceIndex.value - 1)
    }
  }

  function jumpNext() {
    if (canJumpNext.value) {
      jumpToSentence(currentSentenceIndex.value + 1)
    }
  }

  function jumpLast() {
    jumpToSentence(sentences.value.length - 1)
  }

  // Auto-scroll when current sentence changes
  watch(currentSentenceIndex, () => {
    nextTick(() => {
      if (typeof document === 'undefined') {
        return
      }
      const localIndex = currentSentenceIndex.value - visibleStart.value
      const el = document.getElementById(`${PLAYBACK_OVERLAY_TEXT_NAME}:${localIndex}${SENTENCE_ELEMENT_ID_SUFFIX}`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    })
  })

  // --- Public API ---

  /** Fetch section sentences into the store if not already loaded. */
  function ensureSectionSentencesLoaded(rec: PracticeRecordingType) {
    const fp = (rec.focusPoints?.filter(Boolean) ?? [])[0] as PracticeRecordingFocusPointType | undefined
    if (!fp || sectionSentences(fp).length) {
      return
    }
    if (fp.aliyah?.id) {
      store.dispatch.student.fetchAliyahSentences({ aliyah: fp.aliyah })
    } else if (fp.haftarah?.reading?.id) {
      store.dispatch.student.fetchHaftarahSentences({ haftarah: fp.haftarah })
    } else if (fp.sentenceGroup?.reading?.id) {
      store.dispatch.student.fetchSentenceGroupSentences({ sentenceGroup: fp.sentenceGroup })
    }
  }

  async function openOverlay(rec: PracticeRecordingType) {
    // Show overlay immediately with loading indicator while audio fetches
    recording.value = rec
    currentFocusIndex.value = 0
    currentSegmentPointIndex.value = 0
    overlayFontSize.value = storedFontSize.value
    surroundLines.value = maxSurroundLines.value
    loading.value = true
    visible.value = true
    ensureSectionSentencesLoaded(rec)
    console.log(
      `[openOverlay] focusPoints=${rec.focusPoints?.length} segmentPoints=${rec.segmentPoints?.length}`,
      `focusMoments=${rec.focusPoints?.map((fp) => fp?.moment).join(',')}`,
      `segmentMoments=${rec.segmentPoints?.map((sp) => sp?.moment).join(',')}`,
    )
    const blobUrl = await store.dispatch.practiceRecordings.fetchRecordingAudio(rec.id)
    loading.value = false
    if (!blobUrl) {
      closeOverlay()
      store.dispatch.dialog.show({ message: 'Could not load recording audio', title: 'Error' })
      return
    }
    // Mark as listened for the teacher (no-op if already listened or if caller is the student)
    if (!rec.listenedAt) {
      store.dispatch.practiceRecordings.markListened(rec.id)
    }
  }

  function closeOverlay() {
    visible.value = false
    recording.value = null
  }

  async function downloadRecording(rec: PracticeRecordingType) {
    const blobUrl = await store.dispatch.practiceRecordings.fetchRecordingAudio(rec.id)
    if (!blobUrl) {
      store.dispatch.dialog.show({ message: 'Could not load recording audio', title: 'Error' })
      return
    }
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = `recording-${practiceRecordingDescription(rec) || rec.id}-${formattedDate(rec.created)}.wav`
    a.click()
  }

  return {
    audioSrc,
    canJumpNext,
    canJumpPrev,
    clearPendingChoice,
    clickableSegment,
    closeOverlay,
    currentSentenceIndex,
    downloadRecording,
    handleSegmentClick,
    jumpFirst,
    jumpLast,
    jumpNext,
    jumpPrev,
    loading: computed(() => loading.value),
    maxSurroundLines,
    onAudioTimeUpdate,
    openOverlay,
    overlayFontSize: computed({
      get: () => overlayFontSize.value,
      set: (v: number) => {
        overlayFontSize.value = v
      },
    }),
    pendingOccurrenceChoice: computed(() => pendingOccurrenceChoice.value),
    recording: computed({
      get: () => recording.value,
      set: (v: PracticeRecordingType | null) => {
        recording.value = v
      },
    }),
    seekGeneration: computed(() => seekGeneration.value),
    seekTimeMs: computed(() => seekTimeMs.value),
    seekToOccurrence,
    segmentStyle,
    segmentations,
    sentenceStyle,
    startIndex,
    suppressColors,
    surroundLines: computed({
      get: () => surroundLines.value,
      set: (v: number) => {
        surroundLines.value = v
      },
    }),
    totalSentences: computed(() => sentences.value.length),
    visible: computed(() => visible.value),
    visibleSentences,
  }
}
